import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import KycUploadModal from '../components/KycUploadModal';
import {
  ArrowLeft, User, Lightning, Receipt, ChatCircleText, ShieldCheck,
  FileText, CheckCircle, XCircle, Clock, Buildings, Pulse, Warning, Spinner,
  IdentificationCard, Upload,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  lead: 'Lead', pre_contratto: 'Pre-Contratto', firmato: 'Firmato',
  in_verifica: 'In Verifica', verifica_doc_ok: 'Doc OK', verifica_doc_ko: 'Doc KO',
  credit_check_ok: 'Credit OK', credit_check_ko: 'Credit KO',
  attivo: 'Attivo', rifiutato: 'Rifiutato', annullato: 'Annullato',
};

const STATUS_COLORS = {
  lead: 'bg-slate-100 text-slate-700',
  pre_contratto: 'bg-blue-100 text-blue-700',
  firmato: 'bg-purple-100 text-purple-700',
  in_verifica: 'bg-amber-100 text-amber-700',
  verifica_doc_ok: 'bg-cyan-100 text-cyan-700',
  verifica_doc_ko: 'bg-red-100 text-red-700',
  credit_check_ok: 'bg-teal-100 text-teal-700',
  credit_check_ko: 'bg-red-100 text-red-700',
  attivo: 'bg-green-100 text-green-700',
  rifiutato: 'bg-red-100 text-red-700',
};

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState({ system: [], custom: [] });
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [kycMeta, setKycMeta] = useState(null); // {has_file, doc_type, ...}
  const [kycModalOpen, setKycModalOpen] = useState(false);

  const fetchKyc = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/leads/${leadId}/identity-doc`, { credentials: 'include' });
      if (r.ok) setKycMeta(await r.json());
    } catch (e) { /* silent */ }
  }, [leadId]);

  const fetchTimeline = useCallback(async () => {
    setTimelineLoading(true);
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/communications`, { credentials: 'include' });
      if (res.ok) setTimeline(await res.json());
    } catch (e) { /* silent */ }
    finally { setTimelineLoading(false); }
  }, [leadId]);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/full-detail`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else toast.error('Errore caricamento');
    } catch (e) { toast.error('Errore'); }
    finally { setLoading(false); }
  }, [leadId]);

  const fetchDocuments = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/documents`, { credentials: 'include' });
      if (res.ok) setDocuments(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); fetchDocuments(); fetchTimeline(); fetchKyc(); }, [fetchData, fetchDocuments, fetchTimeline, fetchKyc]);

  const updateCte = async (cteDoc) => {
    try {
      const res = await fetch(`${API}/api/pre-contracts/${leadId}/cte`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cte_document: cteDoc }),
      });
      if (res.ok) {
        toast.success(cteDoc ? 'CTE assegnata' : 'CTE rimossa');
        fetchData();
      } else {
        toast.error('Errore aggiornamento CTE');
      }
    } catch (e) { toast.error('Errore aggiornamento CTE'); }
  };

  const promoteToContract = async () => {
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/promote-to-contract`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'manual_upload' }),
      });
      const r = await res.json();
      if (res.ok) {
        toast.success(r.already_exists ? 'Contratto già esistente' : 'Promosso a Pre-Contratto');
        fetchData();
      }
    } catch (e) { toast.error('Errore'); }
  };

  const [ccLoading, setCcLoading] = useState(false);
  const runCreditCheck = async (withScore = false, force = false) => {
    setCcLoading(true);
    try {
      const res = await fetch(`${API}/api/leads/${leadId}/credit-check`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ with_score: withScore, force }),
      });
      const r = await res.json();
      if (res.ok) {
        if (r.cached) toast.info('Dati da cache (24h)');
        else toast.success('Verifica completata');
        fetchData();
      } else {
        toast.error(r.detail || 'Errore Credit Check');
      }
    } catch (e) { toast.error('Errore di rete'); }
    finally { setCcLoading(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento...</div>;
  if (!data) return <div className="p-8 text-red-500">Cliente non trovato</div>;

  const { lead, communications, signature_requests, fattura_analyses, contract, offer, company_settings } = data;
  const fattura = fattura_analyses?.[0];
  const allowPaidCompany = !!company_settings?.openapi_allow_paid_company;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="lead-detail-page">
      <Button variant="ghost" onClick={() => navigate('/leads')} className="mb-4">
        <ArrowLeft size={16} className="mr-2" /> Tutti i clienti
      </Button>

      <div className="bg-white rounded-lg border p-6 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-900">{lead.ragione_sociale || '—'}</h1>
              <Badge className={STATUS_COLORS[lead.status] || ''}>{STATUS_LABELS[lead.status] || lead.status}</Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
              <div><span className="text-slate-500">P.IVA:</span> <strong>{lead.partita_iva || '—'}</strong></div>
              <div><span className="text-slate-500">CF:</span> <strong>{lead.codice_fiscale || '—'}</strong></div>
              <div><span className="text-slate-500">Email:</span> <strong>{lead.email || '—'}</strong></div>
              <div><span className="text-slate-500">Telefono:</span> <strong>{lead.telefono || '—'}</strong></div>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {lead.status === 'attivo' && (
              <Button onClick={() => navigate(`/customers/${lead.id}`)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="open-customer-page-btn">
                Apri Scheda Cliente Completa →
              </Button>
            )}
            {lead.status === 'firmato' && !contract && (
              <Button onClick={promoteToContract} className="bg-orange-500 hover:bg-orange-600" data-testid="promote-contract-btn">
                Promuovi a Pre-Contratto
              </Button>
            )}
            {contract && (
              <Button variant="outline" onClick={() => navigate(`/contracts/${contract.id}`)}>
                <FileText size={14} className="mr-2" /> Vai al Contratto
              </Button>
            )}
          </div>
        </div>
      </div>

      <CreditCheckPanel
        cc={lead.credit_check}
        piva={lead.partita_iva}
        loading={ccLoading}
        onRun={runCreditCheck}
        allowPaidCompany={allowPaidCompany}
      />

      {/* CTE Assignment — same options as Pre-Contratti */}
      <div className="bg-white rounded-lg border p-4 mb-4" data-testid="lead-cte-panel">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Buildings size={18} className="text-amber-500" weight="duotone" />
            <div>
              <div className="text-sm font-semibold text-slate-900">CTE — Condizioni Tecnico-Economiche</div>
              <div className="text-xs text-slate-500">
                {lead.cte_document
                  ? <>Assegnata: <span className="font-mono text-slate-700">{lead.cte_document}</span></>
                  : 'Nessuna CTE assegnata. Selezionane una dall\'elenco.'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={lead.cte_document || ''}
              onChange={(e) => updateCte(e.target.value)}
              className="text-sm border rounded px-2 py-1.5 bg-white min-w-[260px]"
              data-testid="lead-cte-select">
              <option value="">— Nessuna CTE —</option>
              {[
                ...documents.system.filter(d => d.type === 'cte'),
                ...documents.custom.filter(d => d.type === 'cte'),
              ].map(d => (
                <option key={d.filename} value={d.filename}>
                  {d.label} {d.source === 'custom' ? '(custom)' : ''}
                </option>
              ))}
            </select>
            {lead.cte_document && (
              <Button variant="outline" size="sm" onClick={() => updateCte('')}
                      data-testid="lead-cte-remove">
                Rimuovi
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="anagrafica" className="w-full">
        <TabsList className="grid grid-cols-6 mb-4">
          <TabsTrigger value="anagrafica" data-testid="tab-anagrafica"><User size={14} className="mr-1" /> Anagrafica</TabsTrigger>
          <TabsTrigger value="pods" data-testid="tab-pod"><Lightning size={14} className="mr-1" /> POD/PDR ({lead.pods?.length || 0})</TabsTrigger>
          <TabsTrigger value="fattura" data-testid="tab-fatture"><Receipt size={14} className="mr-1" /> Fatture ({fattura_analyses?.length || 0})</TabsTrigger>
          <TabsTrigger value="comms" data-testid="tab-comunicazioni"><ChatCircleText size={14} className="mr-1" /> Timeline ({timeline?.length || 0})</TabsTrigger>
          <TabsTrigger value="signs" data-testid="tab-firma"><ShieldCheck size={14} className="mr-1" /> Firma ({signature_requests?.length || 0})</TabsTrigger>
          <TabsTrigger value="kyc" data-testid="tab-kyc">
            <IdentificationCard size={14} className="mr-1" />
            KYC {kycMeta?.has_file ? <CheckCircle size={12} weight="fill" className="ml-1 text-emerald-500" /> : <Warning size={12} className="ml-1 text-amber-500" />}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="anagrafica" className="bg-white rounded-lg border p-5">
          <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Field label="Ragione Sociale" v={lead.ragione_sociale} />
            <Field label="Partita IVA" v={lead.partita_iva} />
            <Field label="Codice Fiscale" v={lead.codice_fiscale} />
            <Field label="Tipo fornitura" v={lead.tipo_fornitura} />
            <Field label="Email" v={lead.email} />
            <Field label="Telefono" v={lead.telefono} />
            <Field label="Indirizzo sede" v={lead.indirizzo_sede} />
            <Field label="Comune sede" v={lead.comune_sede} />
            <Field label="Fonte lead" v={lead.fonte} />
            <Field label="Data creazione" v={lead.created_at?.substring(0, 19).replace('T', ' ')} />
            <Field label="Consenso marketing" v={lead.consenso_marketing ? '✓' : '—'} />
            <Field label="Consenso terzi" v={lead.consenso_terzi ? '✓' : '—'} />
          </div>
        </TabsContent>

        <TabsContent value="pods" className="bg-white rounded-lg border p-5">
          {(!lead.pods || lead.pods.length === 0) ? (
            <p className="text-slate-400 text-center py-6">Nessun POD/PDR associato</p>
          ) : (
            <div className="space-y-3">
              {lead.pods.map((p, i) => (
                <div key={i} className="border rounded-lg p-4 bg-slate-50">
                  <div className="flex justify-between mb-2">
                    <h4 className="font-bold">{p.pod_code ? `POD: ${p.pod_code}` : `PDR: ${p.pdr_code}`}</h4>
                    {p.fornitore_attuale && <Badge variant="outline">{p.fornitore_attuale}</Badge>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <Field label="Indirizzo" v={p.indirizzo_fornitura} />
                    <Field label="Comune" v={`${p.comune_fornitura || ''} (${p.provincia_fornitura || ''}) ${p.cap_fornitura || ''}`} />
                    <Field label="Potenza impegnata" v={p.potenza_impegnata ? `${p.potenza_impegnata} kW` : '—'} />
                    <Field label="Consumo annuo" v={p.consumo_annuo ? `${p.consumo_annuo} kWh` : '—'} />
                    <Field label="Tipologia uso" v={p.tipologia_uso} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="fattura" className="bg-white rounded-lg border p-5">
          {(!fattura_analyses || fattura_analyses.length === 0) ? (
            <p className="text-slate-400 text-center py-6">Nessuna fattura analizzata</p>
          ) : (
            <div className="space-y-3">
              {fattura_analyses.map(f => {
                const sim = f.simulation;
                const downloadLettera = async () => {
                  try {
                    const r = await fetch(`${API}/api/fatture/analyses/${f.id}/lettera-pdf`, {
                      method: 'POST', credentials: 'include',
                    });
                    if (!r.ok) {
                      const e = await r.json().catch(() => ({}));
                      alert(e.detail || 'Errore download lettera');
                      return;
                    }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                  } catch (err) { alert('Errore'); }
                };
                const sendLetteraEmail = async () => {
                  const toEmail = window.prompt(
                    'Indirizzo email del cliente per l\'invio della Lettera Comparativa:',
                    lead?.email || '',
                  );
                  if (!toEmail || !toEmail.includes('@')) return;
                  const customMsg = window.prompt(
                    'Messaggio personalizzato (opzionale, lascia vuoto per usare il testo standard):',
                    '',
                  );
                  try {
                    const r = await fetch(`${API}/api/fatture/analyses/${f.id}/send-lettera`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to_email: toEmail, message: customMsg || '' }),
                    });
                    const d = await r.json();
                    if (r.ok) toast.success(`Lettera inviata a ${d.to_email}`);
                    else toast.error(d.detail || 'Errore invio');
                  } catch (err) { toast.error('Errore di rete'); }
                };
                return (
                  <div key={f.id} className="border rounded-lg p-4">
                    <div className="flex justify-between mb-2 items-start">
                      <div>
                        <strong>{f.filename}</strong>
                        {f.lettera_pdf_path && (
                          <Badge className="ml-2 bg-purple-100 text-purple-700 border-purple-300">
                            📄 Lettera comparativa allegata
                          </Badge>
                        )}
                        {f.lettera_last_sent_at && (
                          <Badge className="ml-2 bg-green-100 text-green-700 border-green-300">
                            ✉️ Inviata {new Date(f.lettera_last_sent_at).toLocaleDateString('it-IT')}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">{new Date(f.uploaded_at).toLocaleString('it-IT')}</span>
                    </div>
                    {sim && (
                      <div className="bg-orange-50 rounded p-3 text-sm">
                        Risparmio: <strong className={sim.risparmio_annuo_eur > 0 ? 'text-green-700' : 'text-red-700'}>
                          {sim.risparmio_annuo_eur > 0 ? '−' : '+'}{Math.abs(sim.risparmio_annuo_eur).toFixed(2)} €/anno ({Math.abs(sim.risparmio_pct).toFixed(1)}%)
                        </strong>
                      </div>
                    )}
                    {sim && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={downloadLettera}
                                data-testid={`download-lettera-${f.id}`}>
                          📄 {f.lettera_pdf_path ? 'Scarica Lettera Comparativa' : 'Genera Lettera Comparativa'}
                        </Button>
                        {f.lettera_pdf_path && (
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"
                                  onClick={sendLetteraEmail}
                                  data-testid={`send-lettera-${f.id}`}>
                            📧 Invia per email al cliente
                          </Button>
                        )}
                        <span className="text-xs text-slate-500 self-center ml-1">
                          (sarà anche allegata automaticamente all'email del contratto)
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="comms" className="bg-white rounded-lg border p-5" data-testid="timeline-tab">
          {timelineLoading ? (
            <p className="text-slate-400 text-center py-6"><Spinner className="inline animate-spin mr-2" /> Caricamento timeline…</p>
          ) : (!timeline || timeline.length === 0) ? (
            <p className="text-slate-400 text-center py-6">Nessun evento registrato per questo lead</p>
          ) : (
            <div className="relative pl-6" data-testid="lead-timeline">
              {/* Vertical line */}
              <div className="absolute left-2 top-1 bottom-1 w-0.5 bg-slate-200" />
              {timeline.map((ev) => {
                const colorMap = {
                  blue:   { dot: 'bg-blue-500',   ring: 'ring-blue-100',   chip: 'bg-blue-50 text-blue-700 border-blue-200' },
                  green:  { dot: 'bg-green-500',  ring: 'ring-green-100',  chip: 'bg-green-50 text-green-700 border-green-200' },
                  orange: { dot: 'bg-orange-500', ring: 'ring-orange-100', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
                  purple: { dot: 'bg-purple-500', ring: 'ring-purple-100', chip: 'bg-purple-50 text-purple-700 border-purple-200' },
                  amber:  { dot: 'bg-amber-500',  ring: 'ring-amber-100',  chip: 'bg-amber-50 text-amber-700 border-amber-200' },
                  cyan:   { dot: 'bg-cyan-500',   ring: 'ring-cyan-100',   chip: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
                  slate:  { dot: 'bg-slate-500',  ring: 'ring-slate-100',  chip: 'bg-slate-50 text-slate-700 border-slate-200' },
                };
                const c = colorMap[ev.color] || colorMap.slate;
                const dt = ev.datetime ? new Date(ev.datetime) : null;
                const dateStr = dt ? dt.toLocaleString('it-IT', {
                  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                }) : '—';
                return (
                  <div key={ev.id} className="relative mb-5 last:mb-0" data-testid={`timeline-event-${ev.type}`}>
                    <span className={`absolute -left-[18px] top-1 w-3 h-3 rounded-full ${c.dot} ring-4 ${c.ring}`} />
                    <div className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between flex-wrap gap-2">
                        <div className="flex-1 min-w-[220px]">
                          <div className="flex items-center flex-wrap gap-2 mb-1">
                            <span className="font-semibold text-slate-900">{ev.title}</span>
                            <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${c.chip}`}>
                              {ev.type.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                          {ev.description && (
                            <div className="text-sm text-slate-600">{ev.description}</div>
                          )}
                          {ev.actor && (
                            <div className="text-xs text-slate-400 mt-1">
                              <User size={11} className="inline mr-1" /> {ev.actor}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 whitespace-nowrap pl-2">
                          <Clock size={11} className="inline mr-1" /> {dateStr}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="signs" className="bg-white rounded-lg border p-5">
          {(!signature_requests || signature_requests.length === 0) ? (
            <p className="text-slate-400 text-center py-6">Nessuna richiesta firma</p>
          ) : (
            <div className="space-y-3">
              {signature_requests.map(s => (
                <div key={s.id} className="border rounded-lg p-4">
                  <div className="flex justify-between mb-2">
                    <Badge className={s.status === 'signed' ? 'bg-green-100 text-green-700' : 'bg-amber-100'}>
                      {s.status}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {s.signed_at ? `Firmato ${new Date(s.signed_at).toLocaleString('it-IT')}` :
                       `Inviato ${new Date(s.created_at).toLocaleString('it-IT')}`}
                    </span>
                  </div>
                  <div className="text-sm text-slate-600">A: {s.signer_email}</div>
                  {s.signature_metadata?.ip && (
                    <div className="text-xs text-slate-400 mt-1">IP firma: {s.signature_metadata.ip}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kyc" className="bg-white rounded-lg border p-5" data-testid="kyc-tab">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <IdentificationCard size={18} className="text-orange-500" />
                Documento d'identità (KYC)
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Obbligatorio prima dell'invio del contratto al cliente (gate vincolante).
              </p>
            </div>
            <Button onClick={() => setKycModalOpen(true)}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="kyc-open-upload-btn">
              <Upload size={14} className="mr-1" /> {kycMeta?.has_file ? 'Aggiorna' : 'Carica'}
            </Button>
          </div>
          {kycMeta?.has_file ? (
            <div className="border rounded-lg p-4 bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                <span className="font-medium text-emerald-800">KYC completato</span>
                <Badge variant="outline" className="ml-auto border-emerald-300 text-emerald-700">
                  {kycMeta.doc_type}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Numero:</span> <strong>{kycMeta.doc_number || '—'}</strong></div>
                <div><span className="text-slate-500">Scadenza:</span> <strong>{kycMeta.expiry_date || '—'}</strong></div>
                <div className="col-span-2 text-xs text-slate-500">
                  File: {kycMeta.file_name} · {Math.round((kycMeta.size_bytes || 0) / 1024)} KB
                </div>
                <div className="col-span-2 text-xs text-slate-500">
                  Caricato il {(kycMeta.uploaded_at || '').slice(0, 16).replace('T', ' ')} da {kycMeta.uploaded_by}
                </div>
              </div>
              <a href={`${API}/api/leads/${leadId}/identity-doc/file`} target="_blank" rel="noreferrer"
                 className="inline-block mt-3 text-xs text-orange-600 hover:underline"
                 data-testid="kyc-download-link">
                Visualizza/Scarica documento →
              </a>
            </div>
          ) : (
            <div className="border-2 border-dashed border-amber-300 rounded-lg p-6 bg-amber-50 text-center">
              <Warning size={32} className="text-amber-500 mx-auto mb-2" />
              <p className="font-medium text-amber-800">Nessun documento caricato</p>
              <p className="text-xs text-amber-700 mt-1">
                L'invio del contratto verrà bloccato finché non sarà caricato il documento.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <KycUploadModal
        open={kycModalOpen}
        leadId={leadId}
        leadName={lead.ragione_sociale}
        onClose={() => setKycModalOpen(false)}
        onSuccess={() => { setKycModalOpen(false); fetchKyc(); }}
      />
    </div>
  );
}

function Field({ label, v }) {
  return (
    <div className="border-b border-slate-100 py-1.5">
      <span className="text-slate-500 text-xs">{label}</span>
      <div className="font-medium">{v || '—'}</div>
    </div>
  );
}

function CreditCheckPanel({ cc, piva, loading, onRun, allowPaidCompany }) {
  const decision = cc?.decision;
  const score = cc?.score;
  const hasScore = typeof score === 'number';
  const found = cc?.found !== false;

  // Color scheme by decision
  let band = 'slate';
  let bandLabel = 'Non verificato';
  let icon = <Pulse size={20} weight="bold" />;
  if (cc?.error || cc?.success === false) {
    band = 'amber'; bandLabel = 'Errore'; icon = <Warning size={20} weight="bold" />;
  } else if (decision === 'approved') {
    band = 'emerald'; bandLabel = 'Approvato'; icon = <CheckCircle size={20} weight="bold" />;
  } else if (decision === 'base_ok') {
    band = 'emerald'; bandLabel = 'P.IVA attiva (verifica base, senza score)'; icon = <CheckCircle size={20} weight="bold" />;
  } else if (decision === 'manager_review') {
    band = 'amber'; bandLabel = 'Approvazione manageriale'; icon = <Warning size={20} weight="bold" />;
  } else if (decision === 'blocked' || !found) {
    band = 'red'; bandLabel = 'Bloccato'; icon = <XCircle size={20} weight="bold" />;
  }

  const bg = {
    slate: 'bg-slate-50 border-slate-200', emerald: 'bg-emerald-50 border-emerald-300',
    amber: 'bg-amber-50 border-amber-300', red: 'bg-red-50 border-red-300',
  }[band];
  const txt = {
    slate: 'text-slate-700', emerald: 'text-emerald-800',
    amber: 'text-amber-800', red: 'text-red-800',
  }[band];
  const btnPrimary = 'bg-orange-500 hover:bg-orange-600 text-white';
  const btnSecondary = 'bg-white border border-slate-300 hover:bg-slate-50 text-slate-700';

  return (
    <div className={`rounded-lg border-2 p-4 mb-4 ${bg}`} data-testid="credit-check-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`${txt} flex-shrink-0`}>{icon}</div>
          <div>
            <div className={`text-sm font-semibold ${txt}`}>Credit Check Openapi.it</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs ${txt}`} data-testid="credit-check-decision">{bandLabel}</span>
              {hasScore && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  band === 'emerald' ? 'bg-emerald-200 text-emerald-900' :
                  band === 'amber' ? 'bg-amber-200 text-amber-900' :
                  band === 'red' ? 'bg-red-200 text-red-900' : 'bg-slate-200'
                }`} data-testid="credit-check-score">
                  Score: {score}/100
                </span>
              )}
              {cc?.checked_at && (
                <span className="text-[11px] text-slate-500">
                  Verificato {new Date(cc.checked_at).toLocaleString('it-IT')} da {cc.checked_by || '—'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {!piva && (
            <span className="text-xs text-amber-700 italic">Inserisci P.IVA per abilitare</span>
          )}
          {piva && (
            <>
              <button
                onClick={() => onRun(false, !!cc?.checked_at)}
                disabled={loading}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${btnSecondary}`}
                data-testid="credit-check-quick-btn"
                title="Verifica solo presenza P.IVA (gratis/economico)">
                {loading ? <Spinner size={14} className="animate-spin inline" /> : 'Verifica P.IVA'}
              </button>
              {allowPaidCompany && (
                <button
                  onClick={() => onRun(true, true)}
                  disabled={loading}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${btnPrimary}`}
                  data-testid="credit-check-full-btn"
                  title="Verifica + score completo (consuma credito Openapi)">
                  {loading ? <Spinner size={14} className="animate-spin inline" /> : 'Verifica solvibilità'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {cc && (cc.ragione_sociale || cc.fatturato || cc.error) && (
        <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          {cc.ragione_sociale && (
            <Field label="Denominazione (Openapi)" v={cc.ragione_sociale} />
          )}
          {cc.stato_attivita && (
            <Field label="Stato attività" v={cc.stato_attivita} />
          )}
          {cc.indirizzo && (
            <Field label="Sede" v={`${cc.indirizzo}${cc.comune ? `, ${cc.comune}` : ''}${cc.provincia ? ` (${cc.provincia})` : ''}`} />
          )}
          {typeof cc.fatturato === 'number' && cc.fatturato > 0 && (
            <Field label="Fatturato (ultimo bilancio)" v={`€ ${cc.fatturato.toLocaleString('it-IT')}`} />
          )}
          {typeof cc.dipendenti === 'number' && cc.dipendenti > 0 && (
            <Field label="Dipendenti" v={cc.dipendenti} />
          )}
          {typeof cc.equity === 'number' && cc.equity !== 0 && (
            <Field label="Patrimonio netto" v={`€ ${cc.equity.toLocaleString('it-IT')}`} />
          )}
          {cc.error && (
            <div className="col-span-full bg-amber-100 border border-amber-300 rounded p-2 text-amber-900">
              <Warning size={14} className="inline mr-1" /> <strong>Errore:</strong> {cc.error}
            </div>
          )}
          {cc.score_error && (
            <div className="col-span-full bg-amber-100 border border-amber-300 rounded p-2 text-amber-900">
              <Warning size={14} className="inline mr-1" /> Score non disponibile: {cc.score_error}
            </div>
          )}
          {cc.upsell_cerved && (
            <div className="col-span-full bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
              💡 <strong>Suggerimento:</strong> fatturato &gt;€50k. Per uno score Cerved professionale, considera la <a href="https://console.openapi.com" target="_blank" rel="noreferrer" className="underline">Visura Cerved Premium</a>.
            </div>
          )}
          {decision === 'manager_review' && (
            <div className="col-span-full bg-amber-100 border border-amber-300 rounded p-2 text-amber-900 text-xs">
              ⚠️ Score 30-50: contratto richiede approvazione di <strong>direttore commerciale</strong> o <strong>commerciale senior</strong> prima della firma.
            </div>
          )}
          {decision === 'blocked' && (
            <div className="col-span-full bg-red-100 border border-red-300 rounded p-2 text-red-900 text-xs">
              🚫 <strong>Contratto bloccato:</strong> {cc.reason || 'score di solvibilità sotto soglia (&lt;30). Nessun override possibile.'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
